import { Injectable } from '@angular/core';
import { AbilityBuilder, createMongoAbility, MongoAbility } from '@casl/ability';
import { UserRole } from '@qc/shared';
import { User } from '@/app/shared/interfaces';

// Define actions that can be performed
export enum CaslAction {
    Manage = 'manage',
    Create = 'create',
    Read = 'read',
    Update = 'update',
    Delete = 'delete',
}

// Define subjects for RBAC permissions (matching backend)
export enum RbacSubjects {
    CLIENTS = 'clients',
    PROJECTS = 'projects', 
    DOCUMENTS = 'documents',
    PRODUCTS = 'products',
    USERS = 'users',
    SYSTEM_CONFIG = 'system_config',
    ORGANIZATIONS = 'organizations',
}

// Type for our ability
export type AppSubjects = RbacSubjects | 'all';
export type AppAbility = MongoAbility<[CaslAction, AppSubjects]>;

@Injectable({
    providedIn: 'root',
})
export class CaslAbilityFactory {
    createForUser(user: User | null): AppAbility {
        const builder = new AbilityBuilder<AppAbility>(createMongoAbility);
        const { can } = builder;

        if (!user || !user.role) {
            // No user or no role assigned, no permissions
            return builder.build();
        }

        // Convert string role to enum
        const userRole = user.role as UserRole;

        switch (userRole) {
            case UserRole.SYSTEM_ADMIN:
                // System Admin: Manage (Full CRUD) on everything
                can(CaslAction.Manage, RbacSubjects.CLIENTS);
                can(CaslAction.Manage, RbacSubjects.PROJECTS);
                can(CaslAction.Manage, RbacSubjects.DOCUMENTS);
                can(CaslAction.Manage, RbacSubjects.PRODUCTS);
                can(CaslAction.Manage, RbacSubjects.USERS);
                can(CaslAction.Manage, RbacSubjects.SYSTEM_CONFIG);
                can(CaslAction.Manage, RbacSubjects.ORGANIZATIONS);
                break;

            case UserRole.QC_OFFICER:
                // QC Officer permissions based on matrix
                can([CaslAction.Create, CaslAction.Read, CaslAction.Update, CaslAction.Delete], RbacSubjects.CLIENTS);
                can(CaslAction.Manage, RbacSubjects.PROJECTS);
                can([CaslAction.Create, CaslAction.Read, CaslAction.Update, CaslAction.Delete], RbacSubjects.DOCUMENTS);
                can([CaslAction.Create, CaslAction.Read, CaslAction.Update, CaslAction.Delete], RbacSubjects.PRODUCTS);
                can(CaslAction.Read, RbacSubjects.USERS);
                can(CaslAction.Read, RbacSubjects.SYSTEM_CONFIG);
                can([CaslAction.Create, CaslAction.Read, CaslAction.Update, CaslAction.Delete], RbacSubjects.ORGANIZATIONS);
                break;

            case UserRole.AUDITOR:
                // Auditor: Read-only access to everything
                can(CaslAction.Read, RbacSubjects.CLIENTS);
                can(CaslAction.Read, RbacSubjects.PROJECTS);
                can(CaslAction.Read, RbacSubjects.DOCUMENTS);
                can(CaslAction.Read, RbacSubjects.PRODUCTS);
                can(CaslAction.Read, RbacSubjects.USERS);
                can(CaslAction.Read, RbacSubjects.SYSTEM_CONFIG);
                can(CaslAction.Read, RbacSubjects.ORGANIZATIONS);
                break;

            default:
                // Unknown role, no permissions
                break;
        }

        return builder.build({
            detectSubjectType: (subject) => {
                if (subject && typeof subject === 'object' && (subject as any).constructor !== Object) {
                    return (subject as any).constructor.name;
                }
                return subject as unknown as Extract<AppSubjects, string>;
            },
        });
    }

    /**
     * Helper function to get permission summary for a role
     */
    getRolePermissionSummary(role: UserRole): Record<string, string[]> {
        const permissions: Record<string, string[]> = {};

        switch (role) {
            case UserRole.SYSTEM_ADMIN:
                permissions[RbacSubjects.CLIENTS] = ['manage'];
                permissions[RbacSubjects.PROJECTS] = ['manage'];
                permissions[RbacSubjects.DOCUMENTS] = ['manage'];
                permissions[RbacSubjects.PRODUCTS] = ['manage'];
                permissions[RbacSubjects.USERS] = ['manage'];
                permissions[RbacSubjects.SYSTEM_CONFIG] = ['manage'];
                permissions[RbacSubjects.ORGANIZATIONS] = ['manage'];
                break;

            case UserRole.QC_OFFICER:
                permissions[RbacSubjects.CLIENTS] = ['create', 'read', 'update', 'delete'];
                permissions[RbacSubjects.PROJECTS] = ['manage'];
                permissions[RbacSubjects.DOCUMENTS] = ['create', 'read', 'update', 'delete'];
                permissions[RbacSubjects.PRODUCTS] = ['create', 'read', 'update', 'delete'];
                permissions[RbacSubjects.USERS] = ['read'];
                permissions[RbacSubjects.SYSTEM_CONFIG] = ['read'];
                permissions[RbacSubjects.ORGANIZATIONS] = ['create', 'read', 'update', 'delete'];
                break;

            case UserRole.AUDITOR:
                permissions[RbacSubjects.CLIENTS] = ['read'];
                permissions[RbacSubjects.PROJECTS] = ['read'];
                permissions[RbacSubjects.DOCUMENTS] = ['read'];
                permissions[RbacSubjects.PRODUCTS] = ['read'];
                permissions[RbacSubjects.USERS] = ['read'];
                permissions[RbacSubjects.SYSTEM_CONFIG] = ['read'];
                permissions[RbacSubjects.ORGANIZATIONS] = ['read'];
                break;
        }

        return permissions;
    }
}
